// Full-screen overlays for game over (bankruptcy) and victory (Elite club).

import { motion } from 'framer-motion'
import { useGameStore } from '@/store/gameStore'
import { useCareerStore } from '@/store/careerStore'
import { CLUB_LEVELS } from '@/config/clubLevelConfig'
import { DIVISIONS } from '@/config/leagueConfig'
import { Trophy, Skull, RotateCcw, Play, UserX } from 'lucide-react'
import { SPRING_MODAL } from '../motion/motionTokens'
import { AngledButton } from './broadcast'

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(8, 13, 22, 0.92)',
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-surface, #162236)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '14px',
  padding: '2rem',
  maxWidth: '440px',
  width: '90%',
  maxHeight: '90dvh',
  overflowY: 'auto',
  textAlign: 'center',
  boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
}

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-titles)',
  fontSize: '1.5rem',
  fontWeight: 800,
  marginBottom: '0.4rem',
}

function GameStats() {
  const { day, totalIncome, tournamentsCompleted, leagueHistory, clubLevel, reputation } = useGameStore()
  const managerReputation = useCareerStore(s => s.managerReputation)
  const bestLeague = leagueHistory.reduce<string | null>((best, h) => {
    if (!best || h.division < parseInt(best)) return `${h.division}:${h.position}`
    return best
  }, null)

  const stats: Array<[string, string]> = [
    ['Dias al frente del club', String(day)],
    ['Ingresos generados', `${totalIncome.toLocaleString('es-ES')}€`],
    ['Torneos organizados', String(tournamentsCompleted)],
    ['Nivel de club', `${clubLevel} — ${CLUB_LEVELS.find(l => l.level === clubLevel)?.name ?? ''}`],
    ['Reputacion', `${reputation.toFixed(1)} / 5`],
    ['Prestigio de mánager', `${Math.round(managerReputation)} / 100`],
  ]
  if (bestLeague) {
    const [division, position] = bestLeague.split(':')
    stats.push(['Mejor temporada de liga', `${position}º en ${DIVISIONS[parseInt(division)].name}`])
  }

  return (
    <div style={{ margin: '1.25rem 0', textAlign: 'left' }}>
      {stats.map(([label, value]) => (
        <div key={label} style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: '0.8rem', padding: '0.3rem 0',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
          <span style={{ fontWeight: 600 }}>{value}</span>
        </div>
      ))}
    </div>
  )
}

export function GameOverScreen() {
  const gameResult = useGameStore(s => s.gameResult)
  const gameOverReason = useGameStore(s => s.gameOverReason)
  const resetGame = useGameStore(s => s.resetGame)
  const setPlaying = useGameStore(s => s.setPlaying)

  if (gameResult !== 'lost') return null

  const handleNewGame = () => {
    resetGame()
    useCareerStore.getState().reset() // end this career too
    setPlaying(false) // back to main menu
  }

  const fired = gameOverReason === 'fired'

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={overlayStyle}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1, transition: SPRING_MODAL }}
        style={{ ...cardStyle, borderColor: 'rgba(239, 68, 68, 0.4)' }}
      >
        {fired ? (
          <UserX size={42} style={{ color: 'var(--danger, #ef4444)', marginBottom: '0.5rem' }} />
        ) : (
          <Skull size={42} style={{ color: 'var(--danger, #ef4444)', marginBottom: '0.5rem' }} />
        )}
        <h2 style={titleStyle}>{fired ? 'Destituido' : 'Bancarrota'}</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {fired
            ? 'La junta directiva ha perdido la confianza en ti tras incumplir los objetivos. Tu etapa al frente del club termina aquí.'
            : 'El club ha pasado dos semanas en numeros rojos y el banco ha ejecutado la deuda. La aventura termina aqui.'}
        </p>
        <GameStats />
        <AngledButton tone="accent" onClick={handleNewGame}>
          <RotateCcw size={16} /> Nueva partida
        </AngledButton>
      </motion.div>
    </motion.div>
  )
}

export function VictoryScreen() {
  const gameResult = useGameStore(s => s.gameResult)
  const resultAcknowledged = useGameStore(s => s.resultAcknowledged)
  const setResultAcknowledged = useGameStore(s => s.setResultAcknowledged)
  const setPaused = useGameStore(s => s.setPaused)

  if (gameResult !== 'won' || resultAcknowledged) return null

  const handleContinue = () => {
    setResultAcknowledged(true)
    setPaused(false)
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={overlayStyle}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1, transition: SPRING_MODAL }}
        style={{ ...cardStyle, borderColor: 'rgba(251, 191, 36, 0.5)' }}
      >
        <Trophy size={42} style={{ color: 'var(--trophy-gold)', marginBottom: '0.5rem' }} />
        <h2 style={titleStyle}>Club de Elite</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          Lo has conseguido: tu club es el mejor club de padel de la region.
          Has completado el objetivo del modo gestor.
        </p>
        <GameStats />
        <AngledButton tone="gold" onClick={handleContinue}>
          <Play size={16} /> Seguir en modo sandbox
        </AngledButton>
      </motion.div>
    </motion.div>
  )
}
